import FilterList from "@mui/icons-material/FilterList";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import PersonIcon from "@mui/icons-material/Person";
import Search from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Collapse,
  FormControl,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  styled,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import React, { useCallback, useMemo, useState } from "react";
import { useAccount } from "../../../context";
import ActionsMenu from "./components/ActionsMenu";
import CampaignCard from "./components/CampaignCard";
import CampaignVisibleFieldsConfigModal from "./components/CampaignVisibleFieldsConfigModal";
import CopyHistoryModal from "./components/CopyHistoryModal";
import LeadContactsControls from "./components/LeadContactsControls";
import LeadContactsFooter from "./components/LeadContactsFooter";
import LeadContactsHeader from "./components/LeadContactsHeader";
import LeadDetailsModal from "./components/LeadDetailsModal";
import { useLeadContacts } from "./hooks/useLeadContacts";
import { useLeadContactsActions } from "./hooks/useLeadContactsActions";
import { CampaignGroup, LeadContact } from "./types/lead-contacts.types";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 0px;
  margin-bottom: 1px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const EmptyState = styled(Box)`
  display: flex;
  flex-direction: column;
  alignitems: center;
  justifycontent: center;
  padding: 32px 12px;
  text-align: center;
  color: #667781;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  margin: 12px 0;
`;

const ContactSkeleton = styled(Box)`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  background-color: #fafbfc;
`;

const CampaignSkeleton = styled(Box)`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const SearchContainer = styled(Box)`
  padding: 6px;
  background: #f0f2f5;
  border-bottom: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CampaignFilterContainer = styled(Collapse)`
  width: 100%;
  margin-top: 4px;
`;

const SearchInputWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "hasActiveSearch" && prop !== "hasUnsearchedText",
})<{ hasActiveSearch?: boolean; hasUnsearchedText?: boolean }>`
  flex: 1;
  background: ${(props) => {
    if (props.hasActiveSearch) return "#f0f8ff";
    if (props.hasUnsearchedText) return "#fff9e6";
    return "#fff";
  }};
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  border: 1px solid
    ${(props) => {
      if (props.hasActiveSearch) return "#008069";
      if (props.hasUnsearchedText) return "#ffc107";
      return "transparent";
    }};
  transition: all 0.2s ease;
`;

const SearchInput = styled(InputBase)`
  flex: 1;
  font-size: 14px;
  color: #111b21;
  &::placeholder {
    color: #667781;
  }
`;

const ControlsContainer = styled(Box)`
  padding: 8px 12px;
  background: linear-gradient(135deg, #f8fffe 0%, #f0f9f7 100%);
  border-bottom: 1px solid #e8f5e8;
  flexshrink: 0;
`;

// Função para destacar texto na busca
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !text) {
    return text;
  }

  const searchLower = searchTerm.toLowerCase().trim();
  if (!searchLower) {
    return text;
  }

  const textLower = text.toLowerCase();

  if (!textLower.includes(searchLower)) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let index = textLower.indexOf(searchLower, lastIndex);

  while (index !== -1) {
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index));
    }

    const matchLength = searchLower.length;
    const highlightedText = text.substring(index, index + matchLength);

    parts.push(
      <span
        key={`highlight-${index}`}
        style={{
          backgroundColor: "#fff3cd",
          color: "#856404",
          fontWeight: 600,
          padding: "1px 2px",
          borderRadius: "3px",
        }}
      >
        {highlightedText}
      </span>,
    );

    lastIndex = index + matchLength;
    index = textLower.indexOf(searchLower, lastIndex);
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

const LeadContacts: React.FC = () => {
  const { account } = useAccount();
  const {
    state,
    setState,
    fetchContacts,
    fetchContactsByCampaign,
    loadAllByCampaign,
    loadCopyHistory,
    loadCampaignVisibleFields,
  } = useLeadContacts();
  const getConfig = useCallback(
    (campaignId: number) => state.campaignVisibleFieldsConfig[campaignId],
    [state.campaignVisibleFieldsConfig],
  );
  const { copyLeadId, copyContactInfo, copyPhoneNumber, copyBatchContacts } =
    useLeadContactsActions(getConfig);

  // Estados de busca e filtro
  const [searchText, setSearchText] = useState("");
  const [effectiveSearchText, setEffectiveSearchText] = useState("");
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(-1);
  const [selectedCampaignFilter, setSelectedCampaignFilter] =
    useState<string>("");

  // Estados de ordenação e filtros adicionais
  const [sortBy] = useState<"name" | "date" | "status">("name");
  const [sortOrder] = useState<"asc" | "desc">("asc");
  const [showAdditionalFilters, setShowAdditionalFilters] =
    useState<boolean>(false);

  // Estado para o modal de detalhes
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<LeadContact | null>(
    null,
  );
  // Modal de configuração de campos por campanha (admin)
  const [configModalOpen, setConfigModalOpen] = useState(false);

  // Funções auxiliares
  const updateCopiedLeads = useCallback(
    (leadIds: number[]) => {
      setState((prev) => {
        const newSet = new Set(prev.copiedLeadIds);
        leadIds.forEach((id) => newSet.add(id));
        console.log(`🔄 Atualizando leads copiados:`, Array.from(newSet));
        return { ...prev, copiedLeadIds: newSet };
      });
    },
    [setState],
  );

  const clearSelection = useCallback(() => {
    setState((prev) => ({ ...prev, selectedLeads: new Set() }));
  }, [setState]);

  // Handlers para ações
  const handleCopyContact = useCallback(
    (contact: LeadContact) => {
      copyContactInfo(contact, updateCopiedLeads);
    },
    [copyContactInfo, updateCopiedLeads],
  );

  // Handler para mostrar detalhes do lead
  const handleShowDetails = useCallback((contact: LeadContact) => {
    setSelectedContact(contact);
    setShowDetailsModal(true);
  }, []);

  // Handler para fechar modal de detalhes
  const handleCloseDetails = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedContact(null);
  }, []);

  const handleCopyBatch = useCallback(() => {
    const selectedContacts = Object.values(state.groupedContacts)
      .flatMap((campaign) => campaign.contacts)
      .filter((contact) => state.selectedLeads.has(contact.id));

    copyBatchContacts(selectedContacts, updateCopiedLeads, clearSelection);
  }, [
    state.groupedContacts,
    state.selectedLeads,
    copyBatchContacts,
    updateCopiedLeads,
    clearSelection,
  ]);

  // Funções de navegação
  const goToPreviousPage = useCallback(() => {
    if (!state.loading && !state.paginationLoading && state.page > 1) {
      fetchContacts(state.page - 1, true);
    }
  }, [state.loading, state.paginationLoading, state.page, fetchContacts]);

  const goToNextPage = useCallback(() => {
    if (
      !state.loading &&
      !state.paginationLoading &&
      state.page < state.totalPages
    ) {
      fetchContacts(state.page + 1, true);
    }
  }, [
    state.loading,
    state.paginationLoading,
    state.page,
    state.totalPages,
    fetchContacts,
  ]);

  const goToPage = useCallback(
    (targetPage: number) => {
      if (
        targetPage === state.page ||
        targetPage < 1 ||
        targetPage > state.totalPages ||
        state.loading ||
        state.paginationLoading
      ) {
        return;
      }

      fetchContacts(targetPage, true);
    },
    [
      state.page,
      state.totalPages,
      state.loading,
      state.paginationLoading,
      fetchContacts,
    ],
  );

  // Funções de campanha
  const loadMoreByCampaign = useCallback(
    (campaignName: string) => {
      const currentPage = state.campaignPages[campaignName] || 1;
      const isLoading = state.campaignLoading[campaignName];

      if (!isLoading && account?.company_id) {
        fetchContactsByCampaign(campaignName, currentPage + 1, true);
      }
    },
    [
      state.campaignPages,
      state.campaignLoading,
      account?.company_id,
      fetchContactsByCampaign,
    ],
  );

  const loadAllByCampaignHandler = useCallback(
    (campaignName: string) => {
      const isLoading = state.campaignLoading[campaignName];

      if (!isLoading && account?.company_id) {
        loadAllByCampaign(campaignName);
      }
    },
    [state.campaignLoading, account?.company_id, loadAllByCampaign],
  );

  /** Recarrega a config de campos e os leads na página atual (e campanhas expandidas), mantendo a paginação. */
  const handleConfigSaved = useCallback(async () => {
    await loadCampaignVisibleFields();
    await fetchContacts(state.page, true);
    for (const campaignName of state.expandedCampaigns) {
      const page = state.campaignPages[campaignName] ?? 1;
      await fetchContactsByCampaign(campaignName, page, false);
    }
  }, [
    loadCampaignVisibleFields,
    fetchContacts,
    fetchContactsByCampaign,
    state.page,
    state.expandedCampaigns,
    state.campaignPages,
  ]);

  const toggleCampaign = useCallback(
    (campaignName: string) => {
      setState((prev) => {
        const newSet = new Set(prev.expandedCampaigns);
        if (newSet.has(campaignName)) {
          newSet.delete(campaignName);
          return { ...prev, expandedCampaigns: newSet };
        } else {
          newSet.add(campaignName);
          // Carregar contatos da campanha se ainda não foram carregados
          const currentContacts =
            prev.groupedContacts[campaignName]?.contacts || [];
          if (
            currentContacts.length === 0 &&
            !prev.campaignLoading[campaignName]
          ) {
            // Setar loading ANTES de chamar fetchContactsByCampaign para mostrar loading imediatamente
            // Usar setTimeout para garantir que o estado seja atualizado primeiro
            setTimeout(() => {
              fetchContactsByCampaign(
                campaignName,
                1,
                false,
                10,
                state.showCopiedLeads,
              );
            }, 0);
            return {
              ...prev,
              expandedCampaigns: newSet,
              campaignLoading: {
                ...prev.campaignLoading,
                [campaignName]: true,
              },
            };
          }
          return { ...prev, expandedCampaigns: newSet };
        }
      });
    },
    [setState, fetchContactsByCampaign, state.showCopiedLeads],
  );

  const expandAllCampaigns = useCallback(() => {
    const allCampaigns = Object.keys(state.groupedContacts);
    setState((prev) => ({ ...prev, expandedCampaigns: new Set(allCampaigns) }));

    // Carregar contatos das campanhas que ainda não foram carregadas
    allCampaigns.forEach((campaignName, index) => {
      const currentContacts =
        state.groupedContacts[campaignName]?.contacts || [];
      if (
        currentContacts.length === 0 &&
        !state.campaignLoading[campaignName]
      ) {
        setTimeout(() => {
          if (state.isMounted) {
            fetchContactsByCampaign(
              campaignName,
              1,
              false,
              10,
              state.showCopiedLeads,
            );
          }
        }, index * 100);
      }
    });
  }, [
    state.groupedContacts,
    state.campaignLoading,
    state.isMounted,
    setState,
    fetchContactsByCampaign,
    state.showCopiedLeads,
  ]);

  const collapseAllCampaigns = useCallback(() => {
    setState((prev) => ({ ...prev, expandedCampaigns: new Set() }));
  }, [setState]);

  // Funções de seleção
  const handleSelectLead = useCallback(
    (leadId: number) => {
      setState((prev) => {
        const newSet = new Set(prev.selectedLeads);
        if (newSet.has(leadId)) {
          newSet.delete(leadId);
        } else {
          newSet.add(leadId);
        }
        return { ...prev, selectedLeads: newSet };
      });
    },
    [setState],
  );

  const handleSelectAllInCampaign = useCallback(
    (campaignName: string) => {
      const campaignContacts =
        state.groupedContacts[campaignName]?.contacts || [];
      const campaignLeadIds = campaignContacts.map(
        (contact: LeadContact) => contact.id,
      );

      setState((prev) => {
        const newSet = new Set(prev.selectedLeads);
        const allSelected = campaignLeadIds.every((id) => newSet.has(id));

        if (allSelected) {
          campaignLeadIds.forEach((id) => newSet.delete(id));
        } else {
          campaignLeadIds.forEach((id) => newSet.add(id));
        }

        return { ...prev, selectedLeads: newSet };
      });
    },
    [state.groupedContacts, setState],
  );

  const isLeadSelected = useCallback(
    (leadId: number) => state.selectedLeads.has(leadId),
    [state.selectedLeads],
  );

  const isCampaignFullySelected = useCallback(
    (campaignName: string) => {
      const campaignContacts =
        state.groupedContacts[campaignName]?.contacts || [];
      if (campaignContacts.length === 0) return false;
      return campaignContacts.every((contact: LeadContact) =>
        state.selectedLeads.has(contact.id),
      );
    },
    [state.groupedContacts, state.selectedLeads],
  );

  const isCampaignPartiallySelected = useCallback(
    (campaignName: string) => {
      const campaignContacts =
        state.groupedContacts[campaignName]?.contacts || [];
      if (campaignContacts.length === 0) return false;
      const selectedCount = campaignContacts.filter((contact: LeadContact) =>
        state.selectedLeads.has(contact.id),
      ).length;
      return selectedCount > 0 && selectedCount < campaignContacts.length;
    },
    [state.groupedContacts, state.selectedLeads],
  );

  const isLeadCopied = useCallback(
    (leadId: number) => {
      const numericLeadId = Number(leadId);
      return state.copiedLeadIds.has(numericLeadId);
    },
    [state.copiedLeadIds],
  );

  // Funções de modal e menu
  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setState((prev) => ({
        ...prev,
        anchorEl: event.currentTarget,
        menuOpen: true,
      }));
    },
    [setState],
  );

  const handleMenuClose = useCallback(() => {
    setState((prev) => ({ ...prev, menuOpen: false, anchorEl: null }));
  }, [setState]);

  const handleHistoryClick = useCallback(() => {
    handleMenuClose();
    setState((prev) => ({ ...prev, showHistoryModal: true }));
    // O histórico será carregado quando o modal abrir via onFilterChange
  }, [handleMenuClose, setState]);

  const closeHistoryModal = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showHistoryModal: false,
      copyHistory: [],
      historyPage: 1,
      historyTotal: 0,
      historyHasMore: false,
    }));
  }, [setState]);

  const handleFilterChange = useCallback(
    (
      filters: {
        leadId?: number;
        coordId?: number;
        campaignName?: string;
        origin?: "normal" | "repescagem";
      },
      page: number = 1,
    ) => {
      loadCopyHistory(filters, page);
    },
    [loadCopyHistory],
  );

  const toggleCopiedLeads = useCallback(() => {
    const nextShowCopied = !state.showCopiedLeads;
    setState((prev) => ({ ...prev, showCopiedLeads: nextShowCopied }));
    // Sempre recarregar as campanhas expandidas para aplicar o novo filtro de visibilidade
    if (state.expandedCampaigns.size > 0) {
      state.expandedCampaigns.forEach((campaignName) => {
        const campaignTotal = state.groupedContacts[campaignName]?.total || 10;
        fetchContactsByCampaign(
          campaignName,
          1,
          false,
          Math.max(campaignTotal, 10),
          nextShowCopied,
        );
      });
    }
  }, [
    state.showCopiedLeads,
    state.expandedCampaigns,
    state.groupedContacts,
    setState,
    fetchContactsByCampaign,
  ]);

  // Lista de campanhas para o filtro
  const campaigns = useMemo(() => {
    return Object.keys(state.groupedContacts).map((campaignName) => ({
      id: state.groupedContacts[campaignName]?.contacts[0]?.campaign_id || 0,
      name: campaignName,
    }));
  }, [state.groupedContacts]);

  // Garantir que a campanha selecionada no filtro esteja sempre disponível no Select
  const availableCampaignsForSelect = useMemo(() => {
    if (!selectedCampaignFilter) {
      return campaigns;
    }

    const isSelectedCampaignInList = campaigns.some(
      (c) => c.name === selectedCampaignFilter,
    );

    if (isSelectedCampaignInList) {
      return campaigns;
    }

    return [{ id: -1, name: selectedCampaignFilter }, ...campaigns];
  }, [campaigns, selectedCampaignFilter]);

  // Filtrar e ordenar contatos baseado na busca, filtro de campanha e filtros adicionais
  const filteredGroupedContacts = useMemo(() => {
    const searchLower = effectiveSearchText.toLowerCase().trim();
    const hasSearch = searchLower.length > 0;
    const hasCampaignFilter = selectedCampaignFilter.length > 0;
    const hasAnyFilter = hasSearch || hasCampaignFilter;

    const result: Record<string, CampaignGroup> = {};

    Object.entries(state.groupedContacts).forEach(
      ([campaignName, campaignGroup]) => {
        // Aplicar filtro de campanha
        if (hasCampaignFilter && campaignName !== selectedCampaignFilter) {
          return;
        }

        // Filtrar contatos pela busca e filtros adicionais
        const filteredContacts = campaignGroup.contacts.filter(
          (contact: LeadContact) => {
            // Filtro de busca
            if (hasSearch) {
              const nameMatch =
                contact.name?.toLowerCase().includes(searchLower) ||
                contact.valid_name?.toLowerCase().includes(searchLower);
              const phoneMatch =
                contact.telephone?.toLowerCase().includes(searchLower) ||
                contact.valid_telephone?.toLowerCase().includes(searchLower);
              const emailMatch = contact.email
                ?.toLowerCase()
                .includes(searchLower);
              const campaignMatch = contact.campaign_name
                ?.toLowerCase()
                .includes(searchLower);

              if (!nameMatch && !phoneMatch && !emailMatch && !campaignMatch) {
                return false;
              }
            }

            return true;
          },
        );

        // Ordenar contatos (sempre aplicar ordenação)
        const sortedContacts = [...filteredContacts].sort((a, b) => {
          let comparison = 0;

          switch (sortBy) {
            case "name": {
              const nameA = (a.valid_name ?? a.name ?? "").toLowerCase();
              const nameB = (b.valid_name ?? b.name ?? "").toLowerCase();
              comparison = nameA.localeCompare(nameB);
              break;
            }

            case "date": {
              const dateA = a.last_time_updated
                ? new Date(a.last_time_updated).getTime()
                : 0;

              const dateB = b.last_time_updated
                ? new Date(b.last_time_updated).getTime()
                : 0;

              comparison = dateA - dateB;
              break;
            }

            case "status": {
              comparison = (a.status ?? "").localeCompare(b.status ?? "");
              break;
            }
          }

          return sortOrder === "asc" ? comparison : -comparison;
        });

        // Incluir campanha se tiver contatos filtrados ou se a campanha corresponder à busca
        if (
          sortedContacts.length > 0 ||
          (hasSearch && campaignName.toLowerCase().includes(searchLower)) ||
          !hasAnyFilter
        ) {
          result[campaignName] = {
            ...campaignGroup,
            contacts: sortedContacts,
          };
        }
      },
    );

    return result;
  }, [
    state.groupedContacts,
    effectiveSearchText,
    selectedCampaignFilter,
    sortBy,
    sortOrder,
    state.showCopiedLeads,
  ]);

  // Encontrar todos os contatos que correspondem à busca
  const searchMatches = useMemo(() => {
    if (!effectiveSearchText.trim()) return [];

    const searchLower = effectiveSearchText.toLowerCase().trim();
    const matches: LeadContact[] = [];

    Object.values(filteredGroupedContacts).forEach((campaignGroup) => {
      campaignGroup.contacts.forEach((contact: LeadContact) => {
        const nameMatch =
          contact.name?.toLowerCase().includes(searchLower) ||
          contact.valid_name?.toLowerCase().includes(searchLower);
        const phoneMatch =
          contact.telephone?.toLowerCase().includes(searchLower) ||
          contact.valid_telephone?.toLowerCase().includes(searchLower);
        const emailMatch = contact.email?.toLowerCase().includes(searchLower);

        if (nameMatch || phoneMatch || emailMatch) {
          matches.push(contact);
        }
      });
    });

    return matches;
  }, [filteredGroupedContacts, effectiveSearchText]);

  // Contar ocorrências totais (contatos + campanhas)
  const totalSearchOccurrences = useMemo(() => {
    if (!effectiveSearchText.trim()) return 0;

    const searchLower = effectiveSearchText.toLowerCase().trim();
    let count = searchMatches.length;

    // Adicionar campanhas que correspondem
    Object.keys(filteredGroupedContacts).forEach((campaignName) => {
      if (campaignName.toLowerCase().includes(searchLower)) {
        count++;
      }
    });

    return count;
  }, [searchMatches, filteredGroupedContacts, effectiveSearchText]);

  // Funções de navegação
  const navigateToNext = useCallback(() => {
    if (totalSearchOccurrences === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % totalSearchOccurrences);
  }, [totalSearchOccurrences]);

  const navigateToPrevious = useCallback(() => {
    if (totalSearchOccurrences === 0) return;
    setCurrentSearchIndex(
      (prev) => (prev - 1 + totalSearchOccurrences) % totalSearchOccurrences,
    );
  }, [totalSearchOccurrences]);

  // Resetar índice quando a pesquisa mudar
  React.useEffect(() => {
    if (effectiveSearchText.trim()) {
      setCurrentSearchIndex(0);
    } else {
      setCurrentSearchIndex(-1);
    }
  }, [effectiveSearchText]);

  // Expandir automaticamente a campanha quando o filtro for selecionado
  React.useEffect(() => {
    if (selectedCampaignFilter) {
      setState((prev) => ({
        ...prev,
        expandedCampaigns: new Set([
          ...prev.expandedCampaigns,
          selectedCampaignFilter,
        ]),
      }));
      // Garantir que a campanha seja carregada
      const currentContacts =
        state.groupedContacts[selectedCampaignFilter]?.contacts || [];
      if (
        currentContacts.length === 0 &&
        !state.campaignLoading[selectedCampaignFilter]
      ) {
        fetchContactsByCampaign(selectedCampaignFilter, 1, false);
      }
    }
  }, [
    selectedCampaignFilter,
    setState,
    state.groupedContacts,
    state.campaignLoading,
    fetchContactsByCampaign,
  ]);

  // Scroll para o item atual da busca
  React.useEffect(() => {
    if (currentSearchIndex < 0 || totalSearchOccurrences === 0) return;

    // Se o índice aponta para um contato
    if (currentSearchIndex < searchMatches.length && searchMatches.length > 0) {
      const contact = searchMatches[currentSearchIndex];
      const campaignName = contact.campaign_name;

      // Expandir a campanha se necessário
      if (!state.expandedCampaigns.has(campaignName)) {
        setState((prev) => ({
          ...prev,
          expandedCampaigns: new Set([...prev.expandedCampaigns, campaignName]),
        }));
        // Garantir que a campanha seja carregada
        const currentContacts =
          state.groupedContacts[campaignName]?.contacts || [];
        if (
          currentContacts.length === 0 &&
          !state.campaignLoading[campaignName]
        ) {
          fetchContactsByCampaign(campaignName, 1, false);
        }
      }

      // Scroll para o contato após um pequeno delay para garantir que a campanha foi expandida
      setTimeout(() => {
        const contactElement = document.querySelector(
          `[data-contact-id="${contact.id}"]`,
        );
        if (contactElement) {
          contactElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    } else {
      // Se o índice aponta para uma campanha
      const searchLower = effectiveSearchText.toLowerCase().trim();
      const matchingCampaigns = Object.keys(filteredGroupedContacts).filter(
        (c) => c.toLowerCase().includes(searchLower),
      );
      const campaignIndex = currentSearchIndex - searchMatches.length;

      if (campaignIndex >= 0 && campaignIndex < matchingCampaigns.length) {
        const campaignName = matchingCampaigns[campaignIndex];

        // Expandir a campanha se necessário
        if (!state.expandedCampaigns.has(campaignName)) {
          setState((prev) => ({
            ...prev,
            expandedCampaigns: new Set([
              ...prev.expandedCampaigns,
              campaignName,
            ]),
          }));
        }

        // Scroll para a campanha
        setTimeout(() => {
          const campaignElement = document.querySelector(
            `[data-campaign-name="${campaignName}"]`,
          );
          if (campaignElement) {
            campaignElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 300);
      }
    }
  }, [
    currentSearchIndex,
    searchMatches,
    totalSearchOccurrences,
    effectiveSearchText,
    filteredGroupedContacts,
    state.expandedCampaigns,
    state.groupedContacts,
    state.campaignLoading,
    setState,
    fetchContactsByCampaign,
  ]);

  if (state.error) {
    return (
      <Container>
        <LeadContactsHeader onMenuOpen={handleMenuOpen} />
        <ContentContainer>
          <BoxWrapper>
            <Alert
              severity="error"
              sx={{
                mt: 2,
                "& .MuiAlert-icon": {
                  color: "#d32f2f",
                },
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Erro ao carregar dados
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "#666" }}>
                {state.error}
              </Typography>
            </Alert>
          </BoxWrapper>
        </ContentContainer>
      </Container>
    );
  }

  return (
    <Container>
      {/* HEADER: Título com dropdown */}
      <LeadContactsHeader onMenuOpen={handleMenuOpen} />

      {/* BUSCA E FILTROS */}
      <SearchContainer>
        <SearchRow>
          <SearchInputWrapper
            hasActiveSearch={effectiveSearchText.trim().length > 0}
            hasUnsearchedText={
              searchText.trim().length > 0 &&
              searchText.trim() !== effectiveSearchText.trim()
            }
          >
            <IconButton
              size="small"
              onClick={() => {
                if (searchText.trim()) {
                  setEffectiveSearchText(searchText.trim());
                }
              }}
              disabled={
                !searchText.trim() ||
                searchText.trim() === effectiveSearchText.trim()
              }
              sx={{
                padding: "4px",
                color:
                  searchText.trim() &&
                  searchText.trim() !== effectiveSearchText.trim()
                    ? "#ff9800"
                    : effectiveSearchText.trim()
                      ? "#008069"
                      : "#54656f",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                },
                "&:disabled": {
                  opacity: 0.4,
                },
                transition: "color 0.2s ease",
              }}
              title={
                searchText.trim() &&
                searchText.trim() !== effectiveSearchText.trim()
                  ? "Pesquisar (ou pressione Enter)"
                  : "Pesquisar"
              }
            >
              <Search sx={{ fontSize: 20 }} />
            </IconButton>
            <SearchInput
              id="lead-contacts-search"
              name="leadContactsSearch"
              placeholder={
                searchText.trim() &&
                searchText.trim() !== effectiveSearchText.trim()
                  ? "Pressione Enter para pesquisar..."
                  : "Pesquisar por nome, telefone, email ou campanha..."
              }
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const hasNewSearchText =
                    searchText.trim() !== effectiveSearchText.trim();

                  if (hasNewSearchText) {
                    e.preventDefault();
                    setEffectiveSearchText(searchText.trim());
                  } else if (
                    e.shiftKey &&
                    effectiveSearchText.trim() &&
                    totalSearchOccurrences > 0
                  ) {
                    navigateToPrevious();
                  } else if (
                    effectiveSearchText.trim() &&
                    totalSearchOccurrences > 0
                  ) {
                    navigateToNext();
                  } else {
                    e.preventDefault();
                    setEffectiveSearchText(searchText.trim());
                  }
                } else if (e.key === "Escape") {
                  setSearchText("");
                  setEffectiveSearchText("");
                }
              }}
              inputProps={{
                "aria-label": "Pesquisar contatos",
                style: {
                  paddingRight:
                    effectiveSearchText.trim() && totalSearchOccurrences > 0
                      ? "140px"
                      : searchText
                        ? "32px"
                        : "0px",
                },
              }}
              sx={{
                flex: 1,
                "& input": {
                  "&::placeholder": {
                    opacity: 0.7,
                    fontSize: "13px",
                    transition: "opacity 0.2s ease",
                  },
                },
              }}
            />
            {searchText.trim() &&
              searchText.trim() !== effectiveSearchText.trim() && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#ff9800",
                    fontSize: "10px",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    position: "absolute",
                    bottom: "-18px",
                    left: "8px",
                    backgroundColor: "#fff9e6",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    border: "1px solid #ffc107",
                    zIndex: 2,
                  }}
                >
                  Pressione Enter para pesquisar
                </Typography>
              )}
            {effectiveSearchText.trim() && totalSearchOccurrences > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  right: searchText ? "32px" : "8px",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  pointerEvents: "auto",
                  zIndex: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#54656f",
                    fontSize: "11px",
                    whiteSpace: "nowrap",
                    fontWeight: 500,
                    mr: 0.5,
                    lineHeight: "24px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {currentSearchIndex + 1} / {totalSearchOccurrences}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigateToPrevious();
                  }}
                  disabled={totalSearchOccurrences === 0}
                  sx={{
                    padding: "2px",
                    minWidth: "24px",
                    width: "24px",
                    height: "24px",
                    color: "#54656f",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    },
                    "&:disabled": {
                      opacity: 0.3,
                    },
                  }}
                  title="Anterior (Shift+Enter)"
                >
                  <KeyboardArrowUp sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigateToNext();
                  }}
                  disabled={totalSearchOccurrences === 0}
                  sx={{
                    padding: "2px",
                    minWidth: "24px",
                    width: "24px",
                    height: "24px",
                    color: "#54656f",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    },
                    "&:disabled": {
                      opacity: 0.3,
                    },
                  }}
                  title="Próximo (Enter)"
                >
                  <KeyboardArrowDown sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
            {searchText && (
              <IconButton
                size="small"
                onClick={() => {
                  setSearchText("");
                  setEffectiveSearchText("");
                }}
                sx={{
                  padding: "2px",
                  minWidth: "24px",
                  width: "24px",
                  height: "24px",
                  color: "#54656f",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
                title="Limpar pesquisa"
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "16px",
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ×
                </Box>
              </IconButton>
            )}
          </SearchInputWrapper>
          {/* Botão para expandir/colapsar filtros (mesmo posicionamento de "Conversas") */}
          <IconButton
            size="small"
            onClick={() => {
              setShowAdditionalFilters(!showAdditionalFilters);
            }}
            sx={{
              padding: "8px",
              color: selectedCampaignFilter ? "#008069" : "#54656f",
              backgroundColor: selectedCampaignFilter
                ? "rgba(0, 128, 105, 0.1)"
                : "transparent",
              "&:hover": {
                backgroundColor: selectedCampaignFilter
                  ? "rgba(0, 128, 105, 0.15)"
                  : "rgba(0, 0, 0, 0.05)",
              },
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
            title={
              selectedCampaignFilter
                ? `Filtro: ${selectedCampaignFilter}`
                : "Filtrar por campanha"
            }
          >
            <FilterList
              sx={{
                fontSize: 20,
                transform: showAdditionalFilters
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </IconButton>
        </SearchRow>
        {/* Todos os filtros (colapsáveis) */}
        <CampaignFilterContainer in={showAdditionalFilters}>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            {/* Filtro de Campanha */}
            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
              <Select
                value={selectedCampaignFilter}
                onChange={(e) => setSelectedCampaignFilter(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: "13px",
                  height: "40px",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#e9edef",
                      borderRadius: "8px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#008069",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#008069",
                    },
                  },
                  "& .MuiSelect-select": {
                    padding: "8px 32px 8px 12px",
                    color: "#111b21",
                  },
                  "& .MuiSelect-icon": {
                    color: "#54656f",
                  },
                }}
              >
                <MenuItem value="">
                  <Typography sx={{ fontSize: "13px", color: "#667781" }}>
                    Todas as Campanhas
                  </Typography>
                </MenuItem>
                {availableCampaignsForSelect.map((campaign) => (
                  <MenuItem
                    key={
                      campaign.id === -1 ? `temp-${campaign.name}` : campaign.id
                    }
                    value={campaign.name}
                  >
                    <Typography sx={{ fontSize: "13px" }}>
                      {campaign.name}
                      {campaign.id === -1 && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "10px",
                            color: "#667781",
                            ml: 0.5,
                            fontStyle: "italic",
                          }}
                        >
                          (outra página)
                        </Typography>
                      )}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </CampaignFilterContainer>
      </SearchContainer>

      {/* CONTROLES: Expandir/Recolher, Seleção */}
      {Object.keys(state.groupedContacts).length > 0 && (
        <ControlsContainer>
          <LeadContactsControls
            groupedContacts={state.groupedContacts}
            expandedCampaigns={state.expandedCampaigns}
            selectedLeads={state.selectedLeads}
            copiedLeadIds={state.copiedLeadIds}
            showCopiedLeads={state.showCopiedLeads}
            onExpandAll={expandAllCampaigns}
            onCollapseAll={collapseAllCampaigns}
            onClearSelection={clearSelection}
            onToggleCopiedLeads={toggleCopiedLeads}
          />
        </ControlsContainer>
      )}

      {/* LISTAGEM: Campanhas e contatos */}
      <ContentContainer>
        <BoxWrapper
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {state.loading && Object.keys(state.groupedContacts).length === 0 ? (
            // Skeleton durante o carregamento inicial
            <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
              {[1, 2, 3].map((index) => (
                <CampaignSkeleton key={`lead-contacts-skeleton-${index}`}>
                  <Box
                    sx={{
                      background:
                        "linear-gradient(135deg, #008069 0%, #006d5b 100%)",
                      color: "#fff",
                      p: 1.5,
                      borderRadius: "8px 8px 0 0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Skeleton
                        variant="text"
                        width="60%"
                        height={20}
                        sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                      />
                      <Skeleton
                        variant="text"
                        width="40%"
                        height={16}
                        sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                      />
                    </Box>
                    <Skeleton
                      variant="circular"
                      width={24}
                      height={24}
                      sx={{ bgcolor: "rgba(255,255,255,0.3)" }}
                    />
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    {[1, 2].map((contactIndex) => (
                      <ContactSkeleton key={contactIndex}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Skeleton variant="rounded" width={80} height={24} />
                          <Skeleton variant="rounded" width={60} height={24} />
                        </Box>
                        <Skeleton variant="text" width="100%" height={16} />
                        <Skeleton variant="text" width="70%" height={16} />
                        <Skeleton variant="text" width="50%" height={16} />
                      </ContactSkeleton>
                    ))}
                  </Box>
                </CampaignSkeleton>
              ))}
            </Box>
          ) : Object.keys(state.groupedContacts).length === 0 ? (
            <EmptyState>
              <Box sx={{ mb: 2 }}>
                <PersonIcon
                  sx={{ fontSize: 48, color: "#008069", opacity: 0.6 }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{ mb: 1, color: "#111b21", fontWeight: 600 }}
              >
                Nenhum contato encontrado
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#667781", textAlign: "center", maxWidth: 300 }}
              >
                Não há contatos disponíveis para você no momento. Tente
                novamente mais tarde.
              </Typography>
            </EmptyState>
          ) : (
            <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
              {(
                Object.entries(filteredGroupedContacts) as [
                  string,
                  CampaignGroup,
                ][]
              ).map(([campaignName, campaignContacts]) => {
                // Verificar se a campanha corresponde à busca
                const searchLower = effectiveSearchText.toLowerCase().trim();
                const isCampaignMatch =
                  !!searchLower &&
                  campaignName.toLowerCase().includes(searchLower);
                const isCurrentCampaignMatch =
                  !!isCampaignMatch &&
                  currentSearchIndex >= searchMatches.length &&
                  currentSearchIndex <
                    searchMatches.length +
                      Object.keys(filteredGroupedContacts).filter((c) =>
                        c.toLowerCase().includes(searchLower),
                      ).length &&
                  Object.keys(filteredGroupedContacts)
                    .filter((c) => c.toLowerCase().includes(searchLower))
                    .indexOf(campaignName) ===
                    currentSearchIndex - searchMatches.length;

                return (
                  <CampaignCard
                    key={campaignName}
                    campaignName={campaignName}
                    campaignContacts={campaignContacts}
                    isExpanded={state.expandedCampaigns.has(campaignName)}
                    isFullySelected={isCampaignFullySelected(campaignName)}
                    isPartiallySelected={isCampaignPartiallySelected(
                      campaignName,
                    )}
                    campaignLoading={state.campaignLoading[campaignName]}
                    hasMore={state.campaignHasMore[campaignName]}
                    onToggle={toggleCampaign}
                    onSelectAll={handleSelectAllInCampaign}
                    onSelectLead={handleSelectLead}
                    onLoadMore={loadMoreByCampaign}
                    onLoadAll={loadAllByCampaignHandler}
                    onCopyContact={handleCopyContact}
                    onCopyPhone={copyPhoneNumber}
                    onCopyLeadId={copyLeadId}
                    onShowDetails={handleShowDetails}
                    isLeadSelected={isLeadSelected}
                    isLeadCopied={isLeadCopied}
                    showCopiedLeads={state.showCopiedLeads}
                    searchText={effectiveSearchText}
                    isSearchMatch={isCampaignMatch}
                    isCurrentMatch={isCurrentCampaignMatch}
                    highlightText={highlightText}
                  />
                );
              })}
            </Box>
          )}
        </BoxWrapper>
      </ContentContainer>

      {/* RODAPÉ: Paginação e ações */}
      <LeadContactsFooter
        selectedLeads={state.selectedLeads}
        loading={state.loading}
        paginationLoading={state.paginationLoading}
        page={state.page}
        totalPages={state.totalPages}
        onCopyBatch={handleCopyBatch}
        onPreviousPage={goToPreviousPage}
        onNextPage={goToNextPage}
        onPageSelect={goToPage}
      />

      {/* Modal de Histórico de Cópias */}
      <CopyHistoryModal
        open={state.showHistoryModal}
        onClose={closeHistoryModal}
        history={state.copyHistory}
        loading={state.historyLoading}
        page={state.historyPage}
        total={state.historyTotal}
        hasMore={state.historyHasMore}
        onFilterChange={handleFilterChange}
      />

      {/* Modal de Detalhes do Lead */}
      <LeadDetailsModal
        open={showDetailsModal}
        contact={selectedContact}
        onClose={handleCloseDetails}
        onCopy={handleCopyContact}
        visibleFieldsConfig={
          selectedContact
            ? state.campaignVisibleFieldsConfig[selectedContact.campaign_id]
            : undefined
        }
      />
      <CampaignVisibleFieldsConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        groupedContacts={state.groupedContacts}
        campaignVisibleFieldsConfig={state.campaignVisibleFieldsConfig}
        onSaved={handleConfigSaved}
      />

      {/* Menu Dropdown */}
      <ActionsMenu
        anchorEl={state.anchorEl}
        open={state.menuOpen}
        onClose={handleMenuClose}
        onHistoryClick={handleHistoryClick}
        onOpenConfig={
          account?.role === "ADMINISTRATOR"
            ? () => setConfigModalOpen(true)
            : undefined
        }
      />
    </Container>
  );
};

export default LeadContacts;
